import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import '../styles/inventory.css';
import '../../rewards/styles/dailyRewardPage.css';
import { useInventory } from '../hooks/useInventoryRedux';
import CharacterTab from '../components/CharacterTab';
import CharacterAssemblyPreview from '../components/CharacterAssemblyPreview';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchLootboxChestTypes, fetchLootboxKeyTypes, fetchLootboxContents, fetchLootboxInventory,
  openChest, clearLastDrop,
} from '../../lootbox/store/lootboxSlice';
import ChestOpeningAnimation from '../../lootbox/components/ChestOpeningAnimation';
import ChestInfoModal from '../../lootbox/components/ChestInfoModal';
import KeyInfoModal from '../../lootbox/components/KeyInfoModal';
import { rarityColor } from '../../lootbox/utils/lootboxRewards';
import { enqueueReward } from '../../spin/store/rewardsQueueSlice';
import { fetchUser } from '../../../store/slices/userSlice';
import TabSwitch from '../../../shared/components/TabSwitch';
import Preloader from '../../../shared/components/Preloader';
import RankBadge from '../components/RankBadge';
import MergeBoardTab from '../components/MergeBoardTab';
import StatChips from '../../../shared/components/StatChips';
import { perkText } from '../../../shared/utils/heroStats';
import { resolveAssetUrl } from '../../../shared/utils/assetUrl';
import '../styles/merge.css';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import { msg } from '../../../i18n/msg';
import { entityText } from '../../../i18n/entityText';
import { partName, rarityLabel } from '../../../shared/utils/gameLabels';

const characterPreviewImage = "/inventory/character.png";

// Event fired by the shared RewardModal's "Open another" button (see RewardCenter)
const OPEN_ANOTHER_EVENT = 'lootbox:open-another';

/**
 * Chest open result → payload of the shared reward queue, so the chest reward uses the
 * same "Congratulations!" popup and fly-to-balance animation as the Lucky Spin.
 */
function chestDropToReward(drop, chestType) {
  const base = { value: Number(drop.value) || 1, source: 'lootbox' };
  let reward;
  switch (drop.rewardType) {
    case 'COINS':
      reward = { ...base, type: 'COINS', label: 'Coins', icon: '/slider/coins_1.png', animationTarget: 'coins', sound: 'gotCoins' };
      break;
    case 'TON':
      reward = { ...base, type: 'TON', label: 'GRAM', icon: '/slider/ton.png', animationTarget: 'ton', sound: 'gotTon' };
      break;
    case 'CHARACTER_MODULE':
      reward = {
        ...base, value: 1, type: 'MODULE', label: 'Character Module', moduleType: drop.moduleType,
        icon: drop.characterModuleImageUrl || '/slider/head.png',
        moduleData: drop.characterModuleImageUrl ? { urlSmall: drop.characterModuleImageUrl } : undefined,
        animationTarget: 'inventory', sound: 'reward',
      };
      break;
    default:
      reward = { ...base, type: drop.rewardType, label: drop.rewardType, animationTarget: 'inventory', sound: 'reward' };
  }
  const left = Math.min(drop.remainingChests ?? 0, drop.remainingKeys ?? 0);
  if (left > 0) {
    reward.secondaryAction = {
      label: `Open another (${left})`,
      labelMsg: msg('lootbox.open_another', 'Open another ({{n}})', { n: left }),
      event: OPEN_ANOTHER_EVENT,
      detail: { chestTypeId: chestType.id },
    };
  }
  return reward;
}

const mainTabItems = [
  { key: 'modules', label: msg('inventory.tab.modules', 'Modules') },
  // Idle Merge board (v1.43.0) — replaced the rank alchemy (3 copies → next rank)
  { key: 'merge', label: msg('inventory.tab.merge', 'Merge') },
  { key: 'cases', label: msg('inventory.tab.cases', 'Cases') },
];

export default function Inventory() {
  const { t } = useTranslation();
  const location = useLocation();
  const [selectedItem, setSelectedItem] = useState(null);
  const [activeTab, setActiveTab] = useState(location.state?.tab || "Character");
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  // `state.mainTab` lets other screens open the Cases tab directly (e.g. BattlePass chest popup)
  const [mainTab, setMainTab] = useState(location.state?.mainTab || 'modules');
  // tracks which chest type is currently being opened (for the wheel + reward modal)
  const [selectedChestType, setSelectedChestType] = useState(null);
  // bumps on every open so "Open another" remounts the wheel from scratch
  const [spinRun, setSpinRun] = useState(0);
  // open request failed — the wheel stops and the chest popup comes back with the error
  const [openError, setOpenError] = useState(null);
  // tracks which chest type's / key type's info modal is open
  const [infoChestType, setInfoChestType] = useState(null);
  const [infoKeyType, setInfoKeyType] = useState(null);

  const dispatch = useDispatch();
  const {
    chestTypes, keyTypes, contentsByChestType, contentsLoaded, typesLoaded, chests, keys, openingChestId, lastDrop,
  } = useSelector(s => s.lootbox);
  // Same number as the Inventory menu badge: chests openable right now (GET /v1/notifications)
  const openableChests = useSelector(s => s.counters.inventory);

  const tabs = useMemo(
    () => mainTabItems.map((tab) => {
      const item = { ...tab, label: t(tab.label.key, tab.label.defaultValue) };
      return tab.key === 'cases' ? { ...item, badge: openableChests } : item;
    }),
    [openableChests, t]
  );

  const {
    loadModules,
    getModulesByType,
    isLoading,
    error,
    markSingleModuleAsViewed
  } = useInventory();

  const getCurrentModules = () => {
    let typeParam = null;
    if (activeTab !== "All") {
      const tabToTypeMap = { "Head": "HEAD", "Body": "BODY", "Hand": "HAND", "Leg": "LEG" };
      typeParam = tabToTypeMap[activeTab];
    }
    // Просто возвращаем модули как есть - бэкенд уже присылает правильную структуру
    return getModulesByType(typeParam);
  };

  const inventory = getCurrentModules();

  // the merge board moves parts in and out of the inventory
  const reloadModules = useCallback(() => loadModules(null), [loadModules]);

  // Quantity lookup maps for lootbox inventory
  const chestQtyByType = useMemo(() => {
    const m = {};
    for (const c of chests) m[c.chestTypeId] = c.quantity;
    return m;
  }, [chests]);

  // Keys are matched to chests by key type (chest_types.key_type_id), like the backend does
  const keyQtyByType = useMemo(() => {
    const m = {};
    for (const k of keys) m[k.keyTypeId] = k.quantity;
    return m;
  }, [keys]);

  // Загружаем инвентарь только один раз при первом входе на страницу.
  // Нужен и на вкладке Character — конструктор героя выбирает части из этого же списка.
  useEffect(() => {
    if (!hasLoadedOnce) {
      // Загружаем все модули сразу
      loadModules(null).then(() => {
        setHasLoadedOnce(true);
      }).catch((err) => {
        console.error('❌ Failed to load inventory on page:', err);
      });
    }
  }, [hasLoadedOnce, loadModules]);

  // Keep the selected stack after a refetch (e.g. after a merge); otherwise select the first one
  useEffect(() => {
    setSelectedItem(prev => {
      const same = prev && inventory.find(m => m.id === prev.id);
      if (same) return same;
      return inventory.length > 0 && inventory[0]?.module ? inventory[0] : null;
    });
  }, [inventory]);

  // Load lootbox data fresh every time Cases tab is opened; drop tables of all
  // chests come in one request, so chest popups open instantly.
  useEffect(() => {
    if (mainTab === 'cases') {
      dispatch(fetchLootboxChestTypes());
      dispatch(fetchLootboxKeyTypes());
      dispatch(fetchLootboxContents());
      dispatch(fetchLootboxInventory());
    }
  }, [mainTab, dispatch]);

  const handleChestClick = (chestType) => {
    if (openingChestId || selectedChestType) return;
    // Always open info modal; the Open button inside the modal triggers actual opening
    setOpenError(null);
    setInfoChestType(chestType);
  };

  // Starts the wheel immediately; the prize is drawn by the backend and the wheel
  // plays it back when the response arrives.
  const startOpening = (chestType) => {
    if (openingChestId) return;
    dispatch(clearLastDrop());
    setOpenError(null);
    setSelectedChestType(chestType);
    setSpinRun(n => n + 1);
    dispatch(openChest(chestType.id))
      .unwrap()
      .catch(err => setOpenError(typeof err === 'string' ? err : i18n.t('lootbox.open_failed', 'Failed to open chest')));
  };

  const handleWheelFailed = () => {
    const chestType = selectedChestType;
    setSelectedChestType(null);
    setInfoChestType(chestType);
  };

  // Wheel stopped → hand the prize to the global reward queue (RewardCenter)
  const handleWheelDone = () => {
    if (lastDrop && selectedChestType) {
      dispatch(enqueueReward(chestDropToReward(lastDrop, selectedChestType)));
      if (lastDrop.rewardType === 'CHARACTER_MODULE') {
        // New module must show up in the Modules tab
        setHasLoadedOnce(false);
        dispatch(fetchUser());
      }
    }
    setSelectedChestType(null);
    dispatch(clearLastDrop());
  };

  // "Open another" in the shared reward popup
  const startOpeningRef = useRef(null);
  startOpeningRef.current = (chestTypeId) => {
    const ct = chestTypes.find(c => c.id === chestTypeId);
    if (ct) startOpening(ct);
  };
  useEffect(() => {
    const onOpenAnother = (e) => startOpeningRef.current?.(e.detail?.chestTypeId);
    window.addEventListener(OPEN_ANOTHER_EVENT, onOpenAnother);
    return () => window.removeEventListener(OPEN_ANOTHER_EVENT, onOpenAnother);
  }, []);

  // Full catalogs are shown (chests and keys), even with qty 0
  const allChestTypes = chestTypes;
  const allKeyTypes = keyTypes;

  const navigate = useNavigate();

  return (
    <div className="selector-container">
      <TabSwitch tabs={tabs} activeTab={mainTab} onTabChange={setMainTab} />

      {/* ── MODULES TAB ─────────────────────────────────────────── */}
      {mainTab === 'modules' ? (
        <>
          <div className="top-section">
            <div className="selected-head">
              {activeTab === "Character" && (
                <>
                  <img className="corner-image top-left" src="/inventory/rotate.png" alt="corner" />
                  <img className="corner-image top-right" src="/inventory/rotate.png" alt="corner" />
                  <img className="corner-image bottom-left" src="/inventory/rotate.png" alt="corner" />
                  <img className="corner-image bottom-right" src="/inventory/rotate.png" alt="corner" />
                </>
              )}

              {activeTab === "Character" ? (
                selectedCharacter ? (
                  <CharacterAssemblyPreview character={selectedCharacter} />
                ) : (
                  <img className="selected-head-img character" src={characterPreviewImage} alt="Character Preview" />
                )
              ) : selectedItem ? (
                <img
                  className="selected-head-img"
                  src={resolveAssetUrl(selectedItem.module?.imageUrl)}
                  alt={selectedItem.module?.type || 'Module'}
                />
              ) : (
                <div className="selected-head-img empty-preview" />
              )}

              <img
                className="plate-img"
                src="/inventory/plate.png"
                alt="plate"
                style={{ opacity: activeTab === "Character" ? "0" : "1" }}
              />

              {/* Selected body part: rank, fuse level, stats and perk */}
              {activeTab !== "Character" && selectedItem && perkText(selectedItem.perkType, selectedItem.perkValue) && (
                <div className="module-preview-perk perk-line">
                  {perkText(selectedItem.perkType, selectedItem.perkValue)}
                </div>
              )}
              {activeTab !== "Character" && selectedItem && (
                <div className="module-preview-bar">
                  <div className="module-preview-info">
                    <RankBadge
                      rank={selectedItem.rank || 0}
                      color={selectedItem.rankColor}
                      name={selectedItem.rankName}
                      badgeImageUrl={selectedItem.rankBadgeImageUrl}
                    />
                    <StatChips str={selectedItem.str} agi={selectedItem.agi} int={selectedItem.int} />
                  </div>
                  <button
                    type="button"
                    className="app-btn app-btn--purple merge-open-btn"
                    onClick={() => setMainTab('merge')}
                  >
                    ⚗ {t('inventory.tab.merge', 'Merge')}
                  </button>
                </div>
              )}
            </div>

            <div className={`tab-bar ${activeTab === "Character" ? "character" : ""}`}>
              <button className={`tab ${activeTab === "All" ? "active" : ""}`} onClick={() => {
                setActiveTab("All");
                setSelectedCharacter(null);
              }}>{t('inventory.filter.all', 'All')}</button>
              <button className={`tab ${activeTab === "Character" ? "active" : ""}`} onClick={() => setActiveTab("Character")}>
                <img className="tab-image" src={activeTab === "Character" ? "/inventory/torso-active.png" : "/inventory/torso.png"} alt="Character" />
              </button>
              <button className={`tab ${activeTab === "Head" ? "active" : ""}`} onClick={() => setActiveTab("Head")}>
                <img className="tab-image" src={activeTab === "Head" ? "/inventory/head-active.png" : "/inventory/head.png"} alt="Head" />
              </button>
              <button className={`tab ${activeTab === "Hand" ? "active" : ""}`} onClick={() => setActiveTab("Hand")}>
                <img className="tab-image" src={activeTab === "Hand" ? "/inventory/hand-active.png" : "/inventory/hand.png"} alt="Hand" />
              </button>
              <button className={`tab ${activeTab === "Body" ? "active" : ""}`} onClick={() => setActiveTab("Body")}>
                <img className="tab-image" src={activeTab === "Body" ? "/inventory/body-active.png" : "/inventory/body.png"} alt="Body" />
              </button>
              <button className={`tab ${activeTab === "Leg" ? "active" : ""}`} onClick={() => setActiveTab("Leg")}>
                <img className="tab-image" src={activeTab === "Leg" ? "/inventory/foot-active.png" : "/inventory/foot.png"} alt="Leg" />
              </button>
            </div>
          </div>

          {activeTab === "Character" ? (
            <CharacterTab
              allModules={getModulesByType(null)}
              onSelectCharacter={setSelectedCharacter}
              selectedCharacterId={selectedCharacter?.id}
            />
          ) : isLoading ? (
            <Preloader />
          ) : error ? (
            <div className="error-text">
              <div className="error-icon">⚠️</div>
              <span>{t('inventory.load_failed', 'Failed to load inventory')}</span>
              <button className="retry-button" onClick={() => { setHasLoadedOnce(false); loadModules(null); }}>
                {t('common.retry', 'Retry')}
              </button>
            </div>
          ) : inventory.length === 0 ? (
            <div className="empty-text">
              <button
                  className="app-btn app-btn--purple"
                  onClick={() => {

                    navigate('/lucky-spin');
                  }}
              >
                {t('inventory.get_modules', 'Get modules in the Game')}
              </button>
            </div>
          ) : (
            <div className="scroll-shadow-wrapper">
              <div className="head-grid">
                {inventory.map((module, index) => (
                  <div
                    key={module.id || index}
                    className={`head-card ${selectedItem?.id === module.id ? "selected" : ""} ${module.isNew ? "new" : ""} ${module.rank > 0 ? "ranked" : ""}`}
                    style={{ '--rank-color': module.rankColor }}
                    onClick={() => {
                      setSelectedItem(module);
                      if (module.isNew) markSingleModuleAsViewed(module.id);
                    }}
                  >
                    <RankBadge
                      rank={module.rank || 0}
                      color={module.rankColor}
                      name={module.rankName}
                      badgeImageUrl={module.rankBadgeImageUrl}
                    />
                    {(module.fuseLevel || 1) > 1 && (
                      <span className="fuse-badge" title={t('inventory.merge_level', 'Merge level')}>
                        {t('lair.level_short', 'Lv {{n}}', { n: module.fuseLevel })}
                      </span>
                    )}
                    <div className="head-card-inner">
                      <div className="head-name">
                        {partName(t, module.module, t('common.unknown', 'Unknown'))}
                      </div>
                      <img src={resolveAssetUrl(module.module?.imageUrl)} alt={module.module?.type || 'Module'} />
                      {/*{module.isNew && <div className="new-badge"/>}*/}
                      {module.quantity > 1 && <div className="quantity-badge">{module.quantity}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : mainTab === 'merge' ? (
        /* ── IDLE MERGE TAB ──────────────────────────────────────── */
        <MergeBoardTab inventory={getModulesByType(null)} onInventoryChanged={reloadModules} />
      ) : (
        /* ── CASES TAB ───────────────────────────────────────────── */
        !typesLoaded && allChestTypes.length === 0 && allKeyTypes.length === 0 ? (
          <Preloader />
        ) : allChestTypes.length === 0 && allKeyTypes.length === 0 ? (
          <div className="empty-text">
            <div className="empty-icon">🗝</div>
            <span>{t('inventory.no_cases', 'No cases available')}</span>
          </div>
        ) : (
          <div className="scroll-shadow-wrapper">
            <div className="head-grid">

              {/* All chest types — show catalog even if not owned */}
              {allChestTypes.map(ct => {
                const chestQty = chestQtyByType[ct.id] || 0;
                const keyQty = keyQtyByType[ct.keyTypeId] || 0;
                const canOpen = chestQty >= 1 && keyQty >= 1;
                const hasChest = chestQty >= 1;
                const busy = openingChestId === ct.id;
                const isLocked = hasChest && !canOpen; // owned but no key
                return (
                  <div
                    key={ct.id}
                    className={`head-card case-chest-card${canOpen ? ' selected' : ''}${!hasChest ? ' case-chest-unowned' : ''}${isLocked ? ' case-chest-locked' : ''}`}
                    onClick={() => handleChestClick(ct)}
                    style={{ opacity: busy ? 0.5 : undefined }}
                  >
                    <div className="head-card-inner">
                      {/* Large chest image fills the card */}
                      <img src={ct.imageUrl} alt={entityText(t, 'chest_type', ct.id, 'name', ct.name)} className="case-chest-img" />

                      {/* Footer: name + number of chests owned */}
                      <div className="case-chest-footer">
                        <span className="case-chest-name">{entityText(t, 'chest_type', ct.id, 'name', ct.name)}</span>
                        <span className={`case-qty${hasChest ? '' : ' case-qty--zero'}`}>{chestQty}</span>
                      </div>
                    </div>

                    {/* Key mark only when this chest can be opened right now */}
                    {canOpen && (
                      <div className="case-ready-key">
                        {ct.keyImageUrl ? <img src={ct.keyImageUrl} alt="key" /> : '🗝'}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* All key types — catalog, 0 shown dimmed */}
              {allKeyTypes.map(kt => {
                const qty = keyQtyByType[kt.id] || 0;
                return (
                  <div
                    key={kt.id}
                    className={`head-card case-chest-card case-key-card${qty > 0 ? '' : ' case-chest-unowned'}`}
                    onClick={() => setInfoKeyType(kt)}
                  >
                    <div className="head-card-inner">
                      <img src={kt.imageUrl || '/key.png'} alt={kt.name || kt.rarity} className="case-chest-img" />
                      <div className="case-chest-footer">
                        <span className="case-chest-name" style={{ color: rarityColor(kt.rarity) }}>
                          {rarityLabel(t, kt.rarity)}
                        </span>
                        <span className={`case-qty${qty > 0 ? '' : ' case-qty--zero'}`}>{qty}</span>
                      </div>
                    </div>
                  </div>
                );
              })}

            </div>
          </div>
        )
      )}

      {/* Wheel of fortune: from tap on Open until it stops on the drawn prize */}
      {selectedChestType && (
        <ChestOpeningAnimation
          key={spinRun}
          chestType={selectedChestType}
          contents={contentsByChestType[selectedChestType.id]}
          drop={lastDrop}
          failed={!!openError}
          onDone={handleWheelDone}
          onFail={handleWheelFailed}
        />
      )}

      {/* Chest info modal: opens for any chest click; Open button inside triggers actual open */}
      {infoChestType && (
        <ChestInfoModal
          chestType={infoChestType}
          contents={contentsByChestType[infoChestType.id]}
          contentsLoading={!contentsLoaded}
          chestQty={chestQtyByType[infoChestType.id] || 0}
          keyQty={keyQtyByType[infoChestType.keyTypeId] || 0}
          error={openError}
          onOpen={() => startOpening(infoChestType)}
          onClose={() => setInfoChestType(null)}
        />
      )}


      {/* Key info modal: what keys are for, owned count or Buy → Shop / Keys */}
      {infoKeyType && (
        <KeyInfoModal
          keyType={infoKeyType}
          quantity={keyQtyByType[infoKeyType.id] || 0}
          onClose={() => setInfoKeyType(null)}
        />
      )}
    </div>
  );
}
